import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { RootState } from '../../redux/types';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import useRefresh from '../../hooks/useRefresh';
import useBackofficeRefresh from '../../hooks/useBackofficeRefresh';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import { singleFlightRefresh } from '../../utils/refreshManager';
import { useProductSyncStream } from '../../hooks/useProductSyncStream';
import { useOrderSyncStream } from '../../hooks/useOrderSyncStream';
import { useBackofficeOrderSyncStream } from '../../hooks/useBackofficeOrderSyncStream';
import { isEmployeeAdminRole } from '../../utils/roles';
import {
  reconcileProducts,
  reconcileOrders,
  reconcileOwnerRecentTransactions,
  reconcileEmployeeRecentTransactions,
} from '../../services/reconcileLoaders';

export default function RealtimeStreamsController() {
  const dispatch = useAppDispatch();
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const axiosBackoffice = useBackofficeAxios();
  const refresh = useRefresh();
  const { refresh: refreshBackoffice } = useBackofficeRefresh();

  const currentLocation = useAppSelector(
    (s: RootState) => s.location.currentLocation as any,
  );
  const employeeData = useAppSelector(
    (s: RootState) => s.auth.inApp.account.employeeInfo,
  );
  const owner = useAppSelector((s: RootState) => s.backofficeOwnerAuth);

  const isAdmin = isEmployeeAdminRole(employeeData?.bp_employee_role);
  const ownerId = String((employeeData as any)?.bp_owner_id || '');

  const handlePosTokenExpired = useCallback(() => {
    void singleFlightRefresh('pos', refresh);
  }, [refresh]);

  const handleBackofficeTokenExpired = useCallback(() => {
    void singleFlightRefresh('backoffice', refreshBackoffice);
  }, [refreshBackoffice]);

  const posEnabled = Boolean(auth?.accessToken && currentLocation?._id);
  const boEnabled = Boolean(
    isAdmin && owner?.accessToken && currentLocation?._id,
  );

  useProductSyncStream({
    enabled: posEnabled,
    baseUrl: String((axiosPrivate.defaults.baseURL as any) || ''),
    accessToken: String(auth?.accessToken || ''),
    locationId: String(currentLocation?._id || ''),
    reconcileProducts: () =>
      reconcileProducts(axiosPrivate, auth, dispatch),
    onTokenExpired: handlePosTokenExpired,
  });

  useOrderSyncStream({
    enabled: posEnabled,
    baseUrl: String((axiosPrivate.defaults.baseURL as any) || ''),
    accessToken: String(auth?.accessToken || ''),
    locationId: String(currentLocation?._id || ''),
    onReconcile: () => {
      reconcileOrders(axiosPrivate, auth, dispatch);
      if (isAdmin && owner?.accessToken && ownerId) {
        reconcileOwnerRecentTransactions(
          axiosBackoffice,
          ownerId,
          owner.accessToken,
          dispatch,
        );
      }
      if (!isAdmin && auth?.accessToken) {
        reconcileEmployeeRecentTransactions(axiosPrivate, auth, dispatch);
      }
    },
    onTokenExpired: handlePosTokenExpired,
  });

  useBackofficeOrderSyncStream({
    enabled: boEnabled,
    baseUrl: String((axiosBackoffice as any)?.defaults?.baseURL || ''),
    accessToken: String(owner?.accessToken || ''),
    locationId: String(currentLocation?._id || ''),
    onReconcile: () => {
      if (owner?.accessToken && ownerId) {
        reconcileOwnerRecentTransactions(
          axiosBackoffice,
          ownerId,
          owner.accessToken,
          dispatch,
        );
      }
    },
    onTokenExpired: handleBackofficeTokenExpired,
  });

  return null;
}
